import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Calendar as BigCalendar, momentLocalizer, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import Loader from "../../../components/Loader/Loader";
import ViewDetailModal from "./view-details-modal";

import { getCalendarOrListBookings } from "../../../actions/admin/my-calendar-actions";
import { notification, onModalClose, onModalOpen } from "../../../utils/utility";
import { getLoggedInUserId, getStaffBusinessId } from "../../../utils/authHelper";
import { mapBookingToCalendarEvent } from "../../../utils/calendarEvents";
import {
  mapCalendarDefaultView,
  mapCalendarFirstDayToMoment,
} from "../../../utils/eventConfigHelpers";
import axios from "../../../axios-instance";
import "../../admin/my-calendar/my-calendar.scss";
import PageHeader from "../../../components/PageHeader/PageHeader";

const localizer = momentLocalizer(moment);

// ─── Custom toolbar ───────────────────────────────────────────────────────────
const CalendarToolbar = ({ label, view, views, onNavigate, onView }) => {
  const viewLabels = { month: "Month", week: "Week", day: "Day" };
  const availableViews = Array.isArray(views) ? views : Object.keys(views || {});

  return (
    <div className="mc-tb">
      <div className="mc-tb__nav">
        <button
          type="button"
          className="mc-tb__arrow"
          aria-label="Previous"
          title="Previous"
          onClick={() => onNavigate("PREV")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          className="mc-tb__arrow"
          aria-label="Next"
          title="Next"
          onClick={() => onNavigate("NEXT")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <h2 className="mc-tb__label" aria-live="polite">{label}</h2>
      </div>

      <div className="mc-tb__right">
        <button type="button" className="mc-tb__today" onClick={() => onNavigate("TODAY")}>
          Today
        </button>
        <div className="mc-tb__views" role="tablist" aria-label="Calendar view">
          {availableViews.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={`mc-tb__view-btn ${view === v ? "active" : ""}`}
              onClick={() => onView(v)}
            >
              {viewLabels[v] || v}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Custom event chip ────────────────────────────────────────────────────────
const CalendarEvent = ({ event }) => (
  <div className="mc-ev">
    <span className="mc-ev__title">{event.title}</span>
    {event.inviteeName ? <span className="mc-ev__meta">{event.inviteeName}</span> : null}
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const StaffCalendar = (props) => {
  const [events, setEvents] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [showViewDetailModal, setShowViewDetailModal] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Calendar state (controlled)
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(Views.MONTH);
  const [timeFormat, setTimeFormat] = useState("12");
  const [configLoaded, setConfigLoaded] = useState(false);

  const selectedMonth = useRef(null);
  const selectedYear = useRef(null);

  const is24 = String(timeFormat) === "24";

  // ── Load calendar settings ──
  useEffect(() => {
    const businessId = getStaffBusinessId();
    if (!businessId) {
      setConfigLoaded(true);
      getBookingsForCalendar();
      return;
    }

    axios
      .get(`/admin/settings/event/${businessId}`)
      .then((res) => {
        const resData = res?.data || {};
        const config = resData?.data?.data || resData?.data || {};

        const firstDay = mapCalendarFirstDayToMoment(config.calendar_first_day);
        moment.updateLocale("en", { week: { dow: firstDay } });

        if (config.time_format) setTimeFormat(String(config.time_format));
        if (config.calendar_default_view) {
          setView(mapCalendarDefaultView(config.calendar_default_view));
        }
        setConfigLoaded(true);
        getBookingsForCalendar();
      })
      .catch(() => {
        setConfigLoaded(true);
        getBookingsForCalendar();
      });

    return () => {
      moment.updateLocale("en", { week: { dow: 0 } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data ──
  const getBookingsForCalendar = (month = null, year = null) => {
    const { getCalendarOrListBookings } = props;
    setPageLoading(true);

    const filtersData = {};
    if (month && year) {
      filtersData.selected_month = month;
      filtersData.selected_year = year;
    }
    filtersData.staff = [{ title: "", value: getLoggedInUserId() }];

    getCalendarOrListBookings({
      data: {
        is_calendar_view: "E",
        filter: { ...filtersData },
      },
      onSuccess: (response) => {
        setPageLoading(false);
        setEvents(response.map(mapBookingToCalendarEvent));
      },
      onError: () => {
        notification.error("An error occurred. Please try later.");
        setPageLoading(false);
      },
    });
  };

  // ── Calendar handlers ──
  const onNavigate = useCallback((newDate) => {
    setDate(newDate);
    const dated = moment(newDate);
    selectedMonth.current = dated.format("MM");
    selectedYear.current = dated.format("YYYY");
    getBookingsForCalendar(dated.format("MM"), dated.format("YYYY"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectEvent = useCallback((event) => {
    setBookingId(event.id);
    setShowViewDetailModal(true);
    onModalOpen();
  }, []);

  const hideViewDetailModal = () => {
    setShowViewDetailModal(false);
    onModalClose();
  };

  // ── Formats (12h / 24h aware) ──
  const timeFmt = is24 ? "HH:mm" : "h:mm A";
  const formats = {
    timeGutterFormat: (date, culture, local) => {
      const mins = date.getMinutes();
      if (mins === 0)  return local.format(date, is24 ? "HH:mm" : "h A",  culture);
      if (mins === 30) return local.format(date, is24 ? "HH:mm" : "h:mm", culture);
      return "";
    },
    eventTimeRangeFormat: ({ start, end }, culture, local) =>
      `${local.format(start, timeFmt, culture)} – ${local.format(end, timeFmt, culture)}`,
    selectRangeFormat: ({ start, end }, culture, local) =>
      `${local.format(start, timeFmt, culture)} – ${local.format(end, timeFmt, culture)}`,
    agendaTimeRangeFormat: ({ start, end }, culture, local) =>
      `${local.format(start, timeFmt, culture)} – ${local.format(end, timeFmt, culture)}`,
    dayHeaderFormat: (d, culture, local) => local.format(d, "dddd, MMMM D", culture),
    dayRangeHeaderFormat: ({ start, end }, culture, local) =>
      `${local.format(start, "MMM D", culture)} – ${local.format(end, "MMM D, YYYY", culture)}`,
  };

  const components = {
    toolbar: CalendarToolbar,
    event: CalendarEvent,
  };

  return (
    <div className="mc-page">
      {showViewDetailModal && (
        <ViewDetailModal
          bookingId={bookingId}
          showViewDetailModal={showViewDetailModal}
          handleClose={hideViewDetailModal}
          loadBookings={() => getBookingsForCalendar(selectedMonth.current, selectedYear.current)}
        />
      )}
      <Loader isShowLoader={pageLoading} />

      <PageHeader
        title="My Calendar"
        subtitle="See your upcoming bookings at a glance."
      />
      <div className="mc-calendar-card">

        {/* Calendar body */}
        <div className="mc-calendar-body">
          {configLoaded && (
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={date}
              view={view}
              onView={setView}
              onNavigate={onNavigate}
              onSelectEvent={onSelectEvent}
              views={[Views.MONTH, Views.WEEK, Views.DAY]}
              components={components}
              formats={formats}
              popup
              showMultiDayTimes
              scrollToTime={moment().set({ hour: 7, minute: 30 }).toDate()}
              step={15}
              timeslots={2}
              style={{ height: "72vh", minHeight: 620 }}
              messages={{
                showMore: (total) => `+${total} more`,
                noEventsInRange: "No bookings in this range.",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default connect(null, {
  getCalendarOrListBookings,
})(StaffCalendar);
